import { buildMedicationLabel } from '../../containers/Inventory/Shared/inventoryFormRules';

type InventoryAuditLineStatus = 'same' | 'changed' | 'only-here';

interface RawAuditLine {
    [key: string]: unknown;
}

interface InventoryAuditEntry {
    identity: string;
    moveType: string;
    label: string;
    lotNumber: string;
    kitNumber: string;
    quantity: number;
}

interface InventoryAuditLine extends InventoryAuditEntry {
    key: string;
    status: InventoryAuditLineStatus;
}

export const INVENTORY_FIELD_TYPES = [
    'PharmacyFormField',
    'ProtocolFormField',
    'ProtocolImpFormField',
    'ProtocolConcomitantFormField'
];

export function isInventoryFieldType(fieldType: unknown): boolean {
    const name = fieldType == null ? '' : fieldType.toString().toLowerCase();
    return INVENTORY_FIELD_TYPES.some(type => type.toLowerCase() === name);
}

// The audited value travels as a dictionary entry, so its keys keep whatever casing the
// serializer used; every read goes through the lowercased copy.
function normalizeKeys(value: unknown): RawAuditLine {
    const normalized: RawAuditLine = {};
    if (value == null || typeof value !== 'object') return normalized;

    Object.entries(value).forEach(([key, entry]) => {
        normalized[key.toLowerCase()] = entry;
    });

    return normalized;
}

function readText(source: RawAuditLine, name: string): string {
    const value = source[name.toLowerCase()];
    return value == null ? '' : value.toString();
}

function toEntry(value: unknown): InventoryAuditEntry {
    const raw = normalizeKeys(value);
    const moveType = readText(raw, 'moveType').toLowerCase();
    const presentation = readText(raw, 'item') || readText(raw, 'externalId');
    const label = buildMedicationLabel({ presentation });
    const lotNumber = readText(raw, 'lotNumber');
    const kitNumber = readText(raw, 'kitNumber');

    return {
        identity: [moveType, label, lotNumber, kitNumber].join('|'),
        moveType,
        label,
        lotNumber,
        kitNumber,
        quantity: Number(raw['quantity']) || 0
    };
}

// Rows audited before the movement carried its own projection stored the raw stock movement,
// with its items nested; an audited line is one item, so those movements are flattened here.
function flattenMovement(raw: RawAuditLine): Array<RawAuditLine> {
    const moveType = readText(raw, 'moveType');
    const items = Array.isArray(raw['items']) ? raw['items'] : [];

    return items.map(entry => {
        const item = normalizeKeys(entry);
        const detail = normalizeKeys(item['item']);
        const confirmedQuantity = Number(item['confirmedquantity']) || 0;

        return {
            moveType,
            item: readText(detail, 'presentation'),
            externalId: readText(detail, 'externalId'),
            lotNumber: readText(normalizeKeys(item['release']), 'lotNumber'),
            kitNumber: readText(normalizeKeys(item['assignment']), 'kitNumber'),
            quantity: confirmedQuantity > 0 ? confirmedQuantity : Number(item['quantity']) || 0
        };
    });
}

function toEntries(value: unknown): Array<InventoryAuditEntry> {
    if (!Array.isArray(value)) return [];

    const entries: Array<InventoryAuditEntry> = [];

    value.forEach(line => {
        const raw = normalizeKeys(line);

        if (Array.isArray(raw['items']))
            flattenMovement(raw).forEach(item => entries.push(toEntry(item)));
        else if (raw['quantity'] != null)
            entries.push(toEntry(raw));
    });

    return entries;
}

function resolveStatus(
    entry: InventoryAuditEntry,
    counterpart: Array<InventoryAuditEntry>
): InventoryAuditLineStatus {
    const twin = counterpart.find(other => other.identity === entry.identity);

    if (twin == null) return 'only-here';
    return twin.quantity === entry.quantity ? 'same' : 'changed';
}

export function resolveInventoryAuditLines(
    value: unknown,
    counterpart: unknown
): Array<InventoryAuditLine> {
    const entries = toEntries(value);
    const others = toEntries(counterpart);

    return entries.map((entry, index) => ({
        ...entry,
        key: `${entry.identity}|${index}`,
        status: resolveStatus(entry, others)
    }));
}
